import type {
  ComparisonApiEnvelope,
  ComparisonPeerList,
  PeerConsensusRatingApiResponse,
  PeerConsensusRatingApiRow,
  PeerMetricApiResponse,
  PeerMetricApiRow,
} from '@app-types/comparisonApi';
import { comparisonClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

export const COMPARISON_PAGE_SIZE = 10;

export interface ComparisonPeerRequestParams {
  ticker: string;
  mkt: string;
  page: number;
  pageSize: number;
}

const toPositiveInteger = (
  value: number | null | undefined,
  fallback: number,
): number => {
  const normalized = Math.floor(Number(value));
  return Number.isFinite(normalized) && normalized > 0
    ? normalized
    : fallback;
};

const toNonNegativeInteger = (
  value: number | null | undefined,
  fallback: number,
): number => {
  const normalized = Math.floor(Number(value));
  return Number.isFinite(normalized) && normalized >= 0
    ? normalized
    : fallback;
};

const normalizePeerList = <T>(
  body: ComparisonApiEnvelope<T>,
  httpStatus: number | undefined,
  params: ComparisonPeerRequestParams,
  fallbackMessage: string,
): ComparisonPeerList<T> => {
  if (body.status !== 0 || !body.data || !Array.isArray(body.data.peers)) {
    throwDataApiError(body, httpStatus, fallbackMessage);
  }

  const peers = body.data.peers;
  return {
    peers,
    total: toNonNegativeInteger(body.data.total, peers.length),
    page: toPositiveInteger(body.data.page, params.page),
    pageSize: toPositiveInteger(body.data.pageSize, params.pageSize),
    updatedTime: body.data.updatedTime ?? null,
  };
};

const fetchComparisonPeers = async <TResponse extends ComparisonApiEnvelope<TRow>, TRow>(
  path: string,
  params: ComparisonPeerRequestParams,
  fallbackMessage: string,
): Promise<ComparisonPeerList<TRow>> => {
  try {
    const response = await comparisonClient.get<TResponse>(path, {
      params: {
        ticker: params.ticker,
        mkt: params.mkt,
        page: params.page,
        pageSize: params.pageSize,
      },
    });

    return normalizePeerList(response.data, response.status, params, fallbackMessage);
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

export const fetchPeerConsensusRating = async (
  params: ComparisonPeerRequestParams,
): Promise<ComparisonPeerList<PeerConsensusRatingApiRow>> =>
  fetchComparisonPeers<PeerConsensusRatingApiResponse, PeerConsensusRatingApiRow>(
    '/peer-consensus-rating',
    params,
    'Peer consensus rating response is invalid.',
  );

export const fetchPeerRevenue = async (
  params: ComparisonPeerRequestParams,
): Promise<ComparisonPeerList<PeerMetricApiRow>> =>
  fetchComparisonPeers<PeerMetricApiResponse, PeerMetricApiRow>(
    '/peer-revenue',
    params,
    'Peer revenue response is invalid.',
  );

export const fetchPeerRecurringProfit = async (
  params: ComparisonPeerRequestParams,
): Promise<ComparisonPeerList<PeerMetricApiRow>> =>
  fetchComparisonPeers<PeerMetricApiResponse, PeerMetricApiRow>(
    '/peer-recurring-profit',
    params,
    'Peer recurring profit response is invalid.',
  );

export const fetchPeerNetIncome = async (
  params: ComparisonPeerRequestParams,
): Promise<ComparisonPeerList<PeerMetricApiRow>> =>
  fetchComparisonPeers<PeerMetricApiResponse, PeerMetricApiRow>(
    '/peer-net-income',
    params,
    'Peer net income response is invalid.',
  );
